/** Shared contracts for the standalone damage lab. Zero physics imports. */

export const BRICK_STATES = ['intact', 'spalled', 'cracked', 'rim', 'removed'] as const;
export type BrickState = (typeof BRICK_STATES)[number];

export const STATE_RANK: Record<BrickState, number> = {
  intact: 0,
  spalled: 1,
  cracked: 2,
  rim: 3,
  removed: 4,
};

export const WEAPONS = ['bullet', 'cannon', 'missile'] as const;
export type WeaponKind = (typeof WEAPONS)[number];

export interface WallSpec {
  width: number;
  height: number;
  brickLength: number;
  brickHeight: number;
  brickDepth: number;
  joint: number;
  seed: number;
}

/** Default ~4.0 × 2.6 m running-bond wall, ~800 instances. */
export const DEFAULT_WALL_SPEC: WallSpec = {
  width: 4.4,
  height: 2.7,
  brickLength: 0.19,
  brickHeight: 0.065,
  brickDepth: 0.09,
  joint: 0.01,
  seed: 1,
};

export type CrackRoutingMode = 'joint-graph' | 'through-unit' | 'radial';
export type AtlasSetId = 'brick-spall-hair' | 'todo-concrete' | 'todo-stone';
export type MaterialId = 'brick' | 'concrete' | 'stone';
export type Rgb = readonly [number, number, number];

/** Per-weapon crack character. Brick cannon = long stair-step; missile = shorter + more. */
export interface WeaponCrackSpec {
  mode: CrackRoutingMode;
  countK: number;
  lengthK: number;
  minCount: number;
  maxCount: number;
  stairBias: number;
  branchChance: number;
}

export interface RimPushInSpec {
  lo: number;
  hi: number;
  missileLo: number;
  missileHi: number;
}

export interface StampRadiiSpec {
  core: number;
  rim: number;
  spall: number;
  missileCoreMul: number;
  missileRimMul: number;
  /** Missile uses this base (not `spall`) so the wider shallow ring stays exact. */
  missileSpall: number;
  missileSpallMul: number;
}

export interface SpallProbSpec {
  cannon: number;
  missile: number;
}

export interface IntegritySpec {
  pock: number;
  spall: number;
  crack: number;
  rim: number;
}

export interface StainSpec {
  minLen: number;
  lenRange: number;
  minWidth: number;
  widthRange: number;
  visMin: number;
}

export interface FilmSpec {
  radiusK: number;
}

export interface ScorchSpec {
  radiusK: number;
}

export interface CrackVisSpec {
  haloPitches: number;
  corePitches: number;
}

export interface DebrisFaceSpec {
  fraction: number;
  cap: number;
  alwaysClayHeads: boolean;
  stretcherClayChance: number;
  backStretcherClayChance: number;
}

export interface PaletteSpec {
  terracotta: Rgb;
  terracottaChunk: Rgb;
  claySalmon: Rgb;
  clayDeep: Rgb;
  mortar: Rgb;
  mortarCrust: Rgb;
  stainGrey: Rgb;
  soot: Rgb;
}

/**
 * Data-driven material signature. Brick is fully populated.
 * Concrete (monolithic radial cracks, cone spall) and stone (block-quantized
 * plates, minimal film) ship as documented stubs — `isStub: true` + `notes`.
 */
export interface DamageMaterialProfile {
  id: MaterialId;
  label: string;
  isStub: boolean;
  notes: string;
  atlasSet: AtlasSetId;
  crackMode: CrackRoutingMode;
  coreK: number;
  rimK: number;
  spallK: number;
  crackCountK: number;
  crackLengthK: number;
  weaponCrack: Record<WeaponKind, WeaponCrackSpec>;
  rimPushIn: RimPushInSpec;
  radii: StampRadiiSpec;
  spallProb: SpallProbSpec;
  integrity: IntegritySpec;
  pockEscalate: number;
  bulletRemoveEnergy: number;
  /** Mirror back-face spall/scorch only when wall thickness ≤ this (m). */
  backFaceMaxDepth: number;
  /** Weaker-than-front scale for the mirrored back-face pattern. */
  backSpallScale: number;
  /** blastFraction above this (and thin wall) writes the back-face mirror. */
  backFaceBlastMin: number;
  stain: StainSpec;
  film: FilmSpec;
  scorch: ScorchSpec;
  crackVis: CrackVisSpec;
  debris: DebrisFaceSpec;
  palette: PaletteSpec;
}

export const WEAPON_PRESETS: Record<WeaponKind, { intensity: number; blastFraction: number }> = {
  bullet: { intensity: 1, blastFraction: 0 },
  cannon: { intensity: 0.25, blastFraction: 0 },
  missile: { intensity: 0.5, blastFraction: 0.85 },
};

export interface StampParams {
  position: readonly [number, number, number] | { x: number; y: number; z: number };
  direction: readonly [number, number, number] | { x: number; y: number; z: number };
  energy: number;
  intensity: number;
  blastFraction: number;
  surfaceNormal: readonly [number, number, number] | { x: number; y: number; z: number };
  profile?: DamageMaterialProfile;
}

export interface LastStampCall {
  position: [number, number, number];
  direction: [number, number, number];
  energy: number;
  intensity: number;
  blastFraction: number;
  surfaceNormal: [number, number, number];
  profileId: MaterialId;
  weapon: WeaponKind;
  removedDelta: number;
  newPaths: number;
}

export interface LedgerStats {
  intact: number;
  spalled: number;
  cracked: number;
  rim: number;
  removed: number;
  pocks: number;
  stamps: number;
  mergedZones: number;
  crackedJoints: number;
  meanIntegrity: number;
  /** Bricks with mirrored back-face spall/scorch (missile + thin wall). */
  backFace: number;
}

export interface DamageWorldStats extends LedgerStats {
  frame: number;
  brickCount: number;
  lastStepMs: number;
  stains: number;
  scorch: number;
  debris: number;
}

export interface DamageWorldOptions {
  wallSpec?: Partial<WallSpec>;
  profile?: DamageMaterialProfile;
}

export function asTriple(
  v: readonly [number, number, number] | { x: number; y: number; z: number },
): [number, number, number] {
  if (Array.isArray(v)) return [v[0], v[1], v[2]];
  const p = v as { x: number; y: number; z: number };
  return [p.x, p.y, p.z];
}

export function escalateState(current: BrickState, next: BrickState): BrickState {
  return STATE_RANK[next] > STATE_RANK[current] ? next : current;
}

export function resolveWeapon(intensity: number, blastFraction: number): WeaponKind {
  const blast = Number.isFinite(blastFraction) ? blastFraction : 0;
  const inten = Number.isFinite(intensity) ? intensity : 0;
  if (blast > 0.5) return 'missile';
  if (inten >= 0.7) return 'bullet';
  return 'cannon';
}
